import React, { useState } from 'react';
import { IconCircleX, IconArchive, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import { updateLeague, archiveLeague, reactivateLeague } from '../../services/leagueService';
import { LEAGUE_STATUS } from '../../data/leagueSports';

const LeagueSettingsModal = ({ league, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: league?.name || '',
    description: league?.description || '',
    maxParticipants: league?.maxParticipants || 16,
    rules: league?.rules || ''
  });
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const isArchived = league?.status === LEAGUE_STATUS.ARCHIVED;
  const isPaidLeague = (league?.maxParticipants || 0) >= 120;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      await updateLeague(league.id, formData);
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    setError('');

    try {
      await archiveLeague(league.id);
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      console.error('Error archiving league:', err);
      setError('Failed to archive league. Please try again.');
    } finally {
      setArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  const handleReactivate = async () => {
    setArchiving(true);
    setError('');

    try {
      await reactivateLeague(league.id);
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      console.error('Error reactivating league:', err);
      setError('Failed to reactivate league. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="ls-settings-modal" onClick={onClose}>
      <div className="ls-settings-content" onClick={e => e.stopPropagation()}>
        <div className="ls-settings-header">
          <h2>League Settings</h2>
          <button onClick={onClose} className="ls-close-btn" type="button">
            <IconCircleX size={28} />
          </button>
        </div>

        <div className="ls-settings-body">
          {error && <div className="ls-error-message">{error}</div>}

          <div className="ls-settings-section">
            <h3>General Settings</h3>

            <div className="ls-form-group">
              <label htmlFor="name">League Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="League name"
              />
            </div>

            <div className="ls-form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="League description"
              />
            </div>

            <div className="ls-form-group">
              <label htmlFor="maxParticipants">Max Participants</label>
              <input
                type="number"
                id="maxParticipants"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleInputChange}
                min="2"
                max="500"
              />
              {formData.maxParticipants >= 120 && (
                <p className="ls-form-note">
                  This league qualifies for paid tier ($1/member/month while active)
                </p>
              )}
            </div>

            <div className="ls-form-group">
              <label htmlFor="rules">Rules</label>
              <textarea
                id="rules"
                name="rules"
                value={formData.rules}
                onChange={handleInputChange}
                rows="4"
                placeholder="League rules and guidelines"
              />
            </div>
          </div>

          <div className="ls-settings-section ls-danger-zone">
            <h3>League Status</h3>

            {isArchived ? (
              <div className="ls-archive-info">
                <div className="ls-archive-badge">
                  <IconArchive size={20} />
                  <span>This league is archived</span>
                </div>
                <p>Archived leagues are hidden from search and billing is paused. All data is preserved.</p>
                <button
                  className="ls-btn-reactivate"
                  onClick={handleReactivate}
                  disabled={archiving}
                >
                  <IconPlayerPlay size={18} />
                  <span>{archiving ? 'Reactivating...' : 'Reactivate League'}</span>
                </button>
                {isPaidLeague && (
                  <p className="ls-billing-note">
                    Reactivating will resume monthly billing of ${formData.maxParticipants}/month.
                  </p>
                )}
              </div>
            ) : (
              <div className="ls-archive-info">
                {!showArchiveConfirm ? (
                  <>
                    <p>Archive this league to pause all activity and billing. Data will be preserved and you can reactivate anytime.</p>
                    <button
                      className="ls-btn-archive"
                      onClick={() => setShowArchiveConfirm(true)}
                    >
                      <IconArchive size={18} />
                      <span>Archive League</span>
                    </button>
                  </>
                ) : (
                  <div className="ls-confirm-archive">
                    <p><strong>Are you sure you want to archive this league?</strong></p>
                    <p>Members won't be able to access the league while it's archived.</p>
                    <div className="ls-confirm-buttons">
                      <button
                        className="ls-btn-cancel"
                        onClick={() => setShowArchiveConfirm(false)}
                        disabled={archiving}
                      >
                        Cancel
                      </button>
                      <button
                        className="ls-btn-confirm-archive"
                        onClick={handleArchive}
                        disabled={archiving}
                      >
                        {archiving ? 'Archiving...' : 'Yes, Archive'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="ls-settings-footer">
          <button className="ls-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="ls-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeagueSettingsModal;
